
import React, { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type UserContextType = {
  user: any | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  refreshUser: () => Promise<void>;
};

// Initialize context with default values
const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  isLoggedIn: false,
  refreshUser: async () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  // These hooks are now inside the functional component body
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = async () => {
    try {
      setIsLoading(true);
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Error getting session:", sessionError);
        setUser(null);
        localStorage.removeItem('user');
        return;
      }
      
      if (session?.user) {
        // Store the full session in localStorage for persistence
        localStorage.setItem('supabase.auth.token', JSON.stringify(session));
        
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single();
          
        if (profileError) {
          console.error("Error fetching user profile:", profileError);
          setUser(null);
        } else {
          const userData = {
            ...session.user,
            ...profile
          };
          
          // Store user data for quick access
          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
        }
      } else {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('supabase.auth.token');
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
      toast.error("Erro ao obter sessão do usuário");
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Try to load user from localStorage first for immediate UI rendering
    const cachedUser = localStorage.getItem('user');
    if (cachedUser) {
      try {
        setUser(JSON.parse(cachedUser));
      } catch (e) {
        console.error("Error parsing cached user:", e);
        localStorage.removeItem('user');
      }
    }
    
    refreshUser();

    // Set up subscription to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        refreshUser();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('supabase.auth.token');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <UserContext.Provider value={{ 
      user, 
      isLoading,
      isLoggedIn: !!user,
      refreshUser 
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
